import { IRarity } from "@/store/apis/catalogsAPI/catalogs.types"
import { IInventoryItem } from "@/store/apis/inventoryAPI/inventory.types"
import { getSortOption } from "./sortOptions"
import {
  IItemListQuery,
  ISearchCore,
  ISearchCoreDeps,
  ISearchIndexEntry,
  ISortSearchContext
} from "./types"

export const ownedCodesKey = (items: IInventoryItem[]): string =>
  Array.from(new Set(items.map((it) => it.definitionCode)))
    .sort()
    .join("|")

export const buildSearchCore = (
  codes: string[],
  { t, exists, language, ready }: ISearchCoreDeps
): ISearchCore => {
  const collator = new Intl.Collator(language)
  if (!ready) {
    return {
      index: [],
      nameByCode: new Map(),
      collator,
      language,
      ready: false
    }
  }

  const index: ISearchIndexEntry[] = codes.map((code) => {
    const nameKey = `item.${code}.name`
    const name = exists(nameKey) ? t(nameKey) : code
    return {
      code,
      nameLower: name.toLocaleLowerCase(language)
    }
  })

  return {
    index,
    nameByCode: new Map(index.map((entry) => [entry.code, entry.nameLower])),
    collator,
    language,
    ready: true
  }
}

export const buildRarityRank = (rarities: IRarity[]): Map<string, number> =>
  new Map(rarities.map((rarity) => [rarity.code, rarity.sortOrder]))

export const buildCurrencyRank = (
  order: readonly string[]
): Map<string, number> => new Map(order.map((code, index) => [code, index]))

export const matchCodes = (
  index: ISearchIndexEntry[],
  needleLower: string
): Set<string> =>
  new Set(
    index
      .filter((entry) => entry.nameLower.includes(needleLower))
      .map((entry) => entry.code)
  )

export const applyItemListQuery = (
  items: IInventoryItem[],
  query: IItemListQuery,
  ctx: ISortSearchContext
): IInventoryItem[] => {
  const needle = query.search.trim().toLocaleLowerCase(ctx.language)
  const matched =
    ctx.ready && needle !== "" ? matchCodes(ctx.index, needle) : null
  const filtered = matched
    ? items.filter((it) => matched.has(it.definitionCode))
    : items
  const option = getSortOption(query.sortKey)
  // .slice() — never sort an RTK-cached array in place
  return filtered.slice().sort((a, b) => option.comparator(a, b, ctx))
}
